/* eslint-disable class-methods-use-this */
import {decorate, observable} from 'mobx';
import PowerClient from "../Models/PowerClient";
import ConnectionStatus from "../Models/ConnectionStatus";
import {ADD_DUMMY_POWER_CLIENTS} from "../../Config/Parameters";
import HeaderTypes from "../Types/HeaderTypes";
import Logger from "../Logger/Logger";
import Utils from "../Utils/Utils";

const logger = Logger.Create("PowerClientRepository");

/**
 * Power Client Repository.
 */
export class PowerClientRepository
{
    /**
     * The connected power clients.
     */
    public powerClients: PowerClient[] = [];

    public currentlySelectedClients: string[] = [];
    
    public dummyPowerClient: PowerClient = {
        id: "",
        createdAt: undefined,
        name: "",
        macAddresses: [],
        powerProxy: undefined,
        isDummy: true,
        isConnected: true,
    };
    
    private currentArrangement = {
        currentType: HeaderTypes.None,
        reverse: false,
    }
    
    /**
     * Updates the connected power clients.
     * @param powerClients The connected power clients.
     */
    public updatePowerClients(powerClients: PowerClient[]): void
    {
        if (ADD_DUMMY_POWER_CLIENTS)
        {
            for (let i = 0; i < powerClients.length; i++)
            {
                powerClients[i].isDummy = false;
            }
        }
        
        this.powerClients = powerClients;
        this.orderArray(this.currentArrangement.currentType);
        
        if (ADD_DUMMY_POWER_CLIENTS)
        {
            this.createDummySpaces(powerClients);
        }
    }
    
    /**
     * Creates dummies to pre-populate the blank spaces.
     * @param currentData
     */
    public createDummySpaces(currentData: PowerClient[]): void
    {
        const maximumEntriesWithoutScrolling = 9;
        
        if (currentData.length > maximumEntriesWithoutScrolling)
        {
            return;
        }
        
        const dummyCount = maximumEntriesWithoutScrolling - currentData.length;
        
        for (let i = 0; i < dummyCount; i++)
        {
            this.powerClients.push(this.dummyPowerClient);
        }
    }

    /**
     * Adds the selected clients to the selected list.
     * @param clientId The client id.
     */
    public addSelectedClient(clientId: string): void
    {
        if (this.currentlySelectedClients.includes(clientId))
        {
            return;
        }
        
        this.currentlySelectedClients.push(clientId);
    }

    /**
     * Removes the selected clients to the selected list.
     * @param clientId The client id.
     */
    public removeSelectedClient(clientId: string): void
    {
        const index = this.currentlySelectedClients.indexOf(clientId);
        
        if (index === -1)
        {
            return;
        }
        
        this.currentlySelectedClients.splice(index, 1);
    }

    /**
     * Adds all the clients to the selected list.
     */
    public addAllSelectedClients(): void
    {
        if (this.powerClients.length === 0)
        {
            return;
        }

        this.currentlySelectedClients = [];
        
        for (let i = 0; i < this.powerClients.length; i++)
        {
            const id = this.powerClients[i].id;
            
            if (id.trim() !== "")
            {
                this.currentlySelectedClients.push(id);
            }
        }
    }

    /**
     * Clears the selected list.
     */
    public clearAllSelectedClients(): void
    {
        this.currentlySelectedClients = [];
    }

    /**
     * Returns if the item is selected.
     * @param id The id to check.
     */
    public isChecked(id: string)
    {
        return this.currentlySelectedClients.includes(id);
    }

    /**
     * Updates the power client connection status.
     * @param connectionStatuses The power proxies connection status.
     */
    public updateConnectionStatuses(connectionStatuses: ConnectionStatus[]): void
    {
        for (let i = 0; i < connectionStatuses.length; i++)
        {
            const matchingObject = this.powerClients.find((item: PowerClient) => item.id === connectionStatuses[i].id);

            if (matchingObject !== undefined)
            {
                matchingObject.isConnected = connectionStatuses[i].isConnected;
            }
        }
    }

    /**
     * Updates the power client array in the order of the header type.
     * @param headerType The header type.
     */
    public updateOrder(headerType: HeaderTypes): void
    {
        if (headerType === this.currentArrangement.currentType)
        {
            this.currentArrangement.reverse = !this.currentArrangement.reverse;
            this.orderArray(headerType);
            return;
        }

        this.orderArray(headerType);
    }
    
    private orderArray(headerType: HeaderTypes)
    {
        let shouldUpdate = false;
        
        switch (headerType)
        {
            case HeaderTypes.ClientName:
                shouldUpdate = this.orderByClientName();
                break;
            case HeaderTypes.ConnectionStatus:
                shouldUpdate = this.orderByConnectionStatus();
                break;
            case HeaderTypes.ProxyName:
                shouldUpdate = this.orderByProxyName();
                break;
            default:
            case HeaderTypes.None:
                break;
        }
        
        if (shouldUpdate)
        {
            this.currentArrangement.currentType = headerType;
        }
    }
    
    private orderByClientName()
    {
        const value = this.currentArrangement.reverse ? -1 : 1;
        const clone = [...this.powerClients];

        const allNames = clone.map((value: PowerClient) => value.name);

        if (Utils.areAllEqual(allNames))
        {
            return false;
        }
        
        clone.sort((a, b) => a.name.toLowerCase() > b.name.toLowerCase()? value : -value);
        this.powerClients = clone;
        
        return true;
    }

    private orderByConnectionStatus()
    {
        const value = this.currentArrangement.reverse ? -1 : 1;
        const clone = [...this.powerClients];
        
        const allConnectionStatus = clone.map((value: PowerClient) => value.isConnected);

        if (Utils.areAllEqual(allConnectionStatus))
        {
            return false;
        }
        
        clone.sort((a, b) => a.isConnected > b.isConnected ? value : -value);
        this.powerClients = clone;
        
        return true;
    }

    private orderByProxyName()
    {
        const value = this.currentArrangement.reverse ? -1 : 1;
        const clone = [...this.powerClients];

        const allProxies = clone.map((value: PowerClient) => value.powerProxy?.name);
        
        if (Utils.areAllEqual(allProxies))
        {
            return false;
        }
        
        clone.sort((a, b) =>
        {
            let aName = "";
            let bName = "";

            if (a.powerProxy !== undefined)
            {
                aName = a.powerProxy.name;
            }

            if (b.powerProxy !== undefined)
            {
                bName = b.powerProxy.name;
            }

            return (
                aName > bName ? value : -value
            );
        });
        
        this.powerClients = clone;
        return true;
    }
}

decorate(PowerClientRepository, {
    powerClients: observable,
    currentlySelectedClients: observable,
});

export const powerClientRepository = new PowerClientRepository();
